import React, { useState, useEffect } from 'react';
import { IndianRupee, CheckCircle, Clock, AlertCircle, TrendingUp, Save, Settings, Eye, Mail, Phone as PhoneIcon } from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';

const API = process.env.REACT_APP_BACKEND_URL;

const req = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Request failed');
  return r.json();
};

const AdminPayments = () => {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [config, setConfig] = useState({ advance_percent: 50, razorpay_configured: false, email_configured: false, sms_configured: false });
  const [loading, setLoading] = useState(true);
  const [newPct, setNewPct] = useState('');
  const [recording, setRecording] = useState(null); // order_id currently being marked paid
  const [toast, setToast] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, o, cfg] = await Promise.all([
        req('/api/payments/summary'),
        req('/api/orders/all'),
        req('/api/payments/config'),
      ]);
      setSummary(s);
      setOrders(o);
      setConfig(cfg);
      setNewPct(String(cfg.advance_percent));
    } catch (e) {
      setToast({ msg: e.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const saveAdvancePercent = async () => {
    try {
      await req('/api/payments/settings', { method: 'PATCH', body: JSON.stringify({ advance_percent: parseFloat(newPct) }) });
      showToast('Advance % updated');
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const recordBalance = async (orderId) => {
    if (!window.confirm(`Mark balance as received for ${orderId}?`)) return;
    setRecording(orderId);
    try {
      await req(`/api/payments/record-balance/${orderId}`, { method: 'POST', body: JSON.stringify({ method: 'cash' }) });
      showToast('Balance recorded');
      load();
    } catch (e) { showToast(e.message, 'error'); }
    setRecording(null);
  };

  const Card = ({ label, value, color, icon: Icon }) => (
    <div className="bg-white border p-3" style={{ borderColor: LINE }}>
      <div className="flex items-center gap-1 mb-1">
        <Icon className="w-3 h-3" style={{ color }} strokeWidth={1.5} />
        <span className="text-[9px] tracking-[0.1em] uppercase" style={{ color: MUTED }}>{label}</span>
      </div>
      <p className="text-[16px]" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
        ₹{(value || 0).toLocaleString('en-IN')}
      </p>
    </div>
  );

  return (
    <div data-testid="admin-payments" className="pb-6">
      {toast && (
        <div className="fixed top-16 right-4 z-50 px-3 py-2 text-[11px] shadow-lg"
          style={{ backgroundColor: toast.type === 'error' ? '#991B1B' : '#166534', color: '#FFF' }}>
          {toast.msg}
        </div>
      )}

      {/* Integration status chips */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className="flex items-center gap-1 px-2 py-1 text-[10px]" style={{ backgroundColor: config.razorpay_configured ? 'rgba(22,101,52,0.08)' : 'rgba(153,27,27,0.08)', color: config.razorpay_configured ? '#166534' : '#991B1B' }}>
          <IndianRupee className="w-2.5 h-2.5" strokeWidth={1.5} /> Razorpay: {config.razorpay_configured ? 'Live' : 'Missing keys'}
        </span>
        <span className="flex items-center gap-1 px-2 py-1 text-[10px]" style={{ backgroundColor: config.email_configured ? 'rgba(22,101,52,0.08)' : 'rgba(139,139,139,0.08)', color: config.email_configured ? '#166534' : MUTED }}>
          <Mail className="w-2.5 h-2.5" strokeWidth={1.5} /> Email: {config.email_configured ? 'Resend' : 'Off'}
        </span>
        <span className="flex items-center gap-1 px-2 py-1 text-[10px]" style={{ backgroundColor: config.sms_configured ? 'rgba(22,101,52,0.08)' : 'rgba(139,139,139,0.08)', color: config.sms_configured ? '#166534' : MUTED }}>
          <PhoneIcon className="w-2.5 h-2.5" strokeWidth={1.5} /> SMS: {config.sms_configured ? 'Twilio' : 'Off'}
        </span>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Card label="Total Value" value={summary?.total_value} color={GOLD} icon={TrendingUp} />
        <Card label="Collected" value={summary?.collected} color="#166534" icon={CheckCircle} />
        <Card label="Pending" value={summary?.pending} color="#991B1B" icon={Clock} />
      </div>

      {/* Advance % control */}
      <div className="bg-white border p-3 mb-3" style={{ borderColor: LINE }}>
        <div className="flex items-center gap-2 mb-2">
          <Settings className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.5} />
          <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Advance Percentage</p>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="advance-pct-input" type="number" min="10" max="100" value={newPct}
            onChange={e => setNewPct(e.target.value)}
            className="flex-1 py-2 px-2 border text-[13px] focus:outline-none focus:border-[#0F0F0F]"
            style={{ borderColor: LINE, color: INK }} />
          <span className="text-[13px]" style={{ color: MUTED }}>%</span>
          <button data-testid="save-advance-pct" onClick={saveAdvancePercent}
            className="px-3 py-2 text-[10px] tracking-[0.08em] uppercase text-white flex items-center gap-1"
            style={{ backgroundColor: INK }}>
            <Save className="w-3 h-3" strokeWidth={1.5} /> Save
          </button>
        </div>
        <p className="text-[9px] mt-1" style={{ color: '#BFBFBF' }}>Applied to all new orders. Existing orders keep their original %.</p>
      </div>

      {/* Order-wise payment table */}
      <div className="bg-white border" style={{ borderColor: LINE }}>
        <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: LINE }}>
          <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: MUTED }}>Order Payments ({orders.length})</p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <p className="p-6 text-center text-[11px]" style={{ color: MUTED }}>Loading…</p>
          ) : orders.length === 0 ? (
            <p className="p-6 text-center text-[11px]" style={{ color: MUTED }}>No orders yet</p>
          ) : (
            orders.map(o => {
              const ps = o.payment_status || 'pending';
              const badgeStyle = ps === 'fully_paid' ? { bg: 'rgba(22,101,52,0.08)', fg: '#166534', label: 'Fully Paid' }
                : ps === 'advance_paid' ? { bg: 'rgba(212,175,55,0.1)', fg: GOLD, label: 'Advance Paid' }
                : { bg: 'rgba(153,27,27,0.08)', fg: '#991B1B', label: 'Pending' };
              return (
                <div key={o.order_id} data-testid={`admin-payment-${o.order_id}`} className="px-3 py-2.5 border-b flex items-center justify-between" style={{ borderColor: '#F0F0F0' }}>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px]" style={{ color: INK, fontWeight: 500 }}>{o.order_id}{o.admin_test && <span data-testid={`payment-admin-test-${o.order_id}`} className="ml-1.5 text-[9px] uppercase" style={{ color: '#991B1B' }}>Admin Test</span>}</p>
                    <p className="text-[10px] truncate" style={{ color: MUTED }}>{o.customer_name} · {o.product}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[9px]">
                      <span style={{ color: MUTED }}>T: ₹{(o.total_price||0).toLocaleString('en-IN')}</span>
                      <span style={{ color: '#166534' }}>A: ₹{(o.advance_amount||0).toLocaleString('en-IN')}</span>
                      <span style={{ color: GOLD }}>B: ₹{(o.balance_amount||0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-[9px] px-1.5 py-0.5 whitespace-nowrap" style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.fg }}>
                      {badgeStyle.label}
                    </span>
                    {ps === 'advance_paid' && (
                      <button data-testid={`record-balance-${o.order_id}`}
                        onClick={() => recordBalance(o.order_id)} disabled={recording === o.order_id}
                        className="text-[9px] px-2 py-0.5 disabled:opacity-50" style={{ backgroundColor: INK, color: '#FFF' }}>
                        {recording === o.order_id ? 'Saving…' : 'Record Balance'}
                      </button>
                    )}
                    {o.notifications_sent && (
                      <div className="flex items-center gap-1 text-[8px]" style={{ color: MUTED }}>
                        <Mail className="w-2 h-2" strokeWidth={1.5} style={{ color: o.notifications_sent.email?.sent ? '#166534' : MUTED }} />
                        <PhoneIcon className="w-2 h-2" strokeWidth={1.5} style={{ color: o.notifications_sent.sms?.sent ? '#166534' : MUTED }} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPayments;
